import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { GraphQLErrorExtensions, GraphQLFormattedError } from 'graphql/error'

interface ErrorParam {
  id?: string
  name?: string
  reportToEmail?: string
  email?: string
  emails?: string[]
  domain?: string
}
export enum CustomErrorCode {
  MFA_RESET_INCOMPLETE = 'MFA_RESET_INCOMPLETE',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  RECENT_LOGIN_REQUIRED = 'RECENT_LOGIN_REQUIRED',
  RATE_LIMITED = 'RATE_LIMITED',
  PASSKEY_NOT_ALLOWED = 'PASSKEY_NOT_ALLOWED',
  BAD_USER_INPUT = 'BAD_USER_INPUT',
  BAD_REQUEST = 'BAD_REQUEST',
  NOT_FOUND = 'NOT_FOUND',
  FORBIDDEN = 'FORBIDDEN',
  DUPLICATED_USER = 'DUPLICATED_USER',
  EMAIL_DOMAIN_NOT_ALLOWED = 'EMAIL_DOMAIN_NOT_ALLOWED',
  DUPLICATE_USER_PHONE_NUMBER = 'DUPLICATE_USER_PHONE_NUMBER',
  MANAGER_RELATION_LOOP = 'MANAGER_RELATION_LOOP',
  FAILED_FETCH_ZOOM_USER = 'FAILED_FETCH_ZOOM_USER',
  INVALID_KAIKURA_KEYS = 'INVALID_KAIKURA_KEYS',
  KAIKURA_CONNECTION_ERROR = 'KAIKURA_CONNECTION_ERROR',
  BATCH_MANAGER_RELATION_LOOP = 'BATCH_MANAGER_RELATION_LOOP',
  BATCH_LICENSE_NOT_ENOUGH = 'BATCH_LICENSE_NOT_ENOUGH',
  BATCH_TARGET_USER_NOT_FOUND = 'BATCH_TARGET_USER_NOT_FOUND',
  BATCH_MANAGER_NOT_FOUND = 'BATCH_MANAGER_NOT_FOUND',
  BATCH_DUPLICATED_USER = 'BATCH_DUPLICATED_USER',
  BATCH_FIREBASE_DUPLICATED_USER = 'BATCH_FIREBASE_DUPLICATED_USER',
  BATCH_EMAIL_DOMAIN_NOT_ALLOWED = 'BATCH_EMAIL_DOMAIN_NOT_ALLOWED',
  RESTRICTED_IP_ERROR = 'RESTRICTED_IP_ERROR',
  ZOOM_USER_NOT_FOUND = 'ZOOM_USER_NOT_FOUND',
  INVALID_BLUE_BEAN_CSV = 'INVALID_BLUE_BEAN_CSV',
  INVALID_BLUE_BEAN_CREDENTIALS = 'INVALID_BLUE_BEAN_CREDENTIALS',
  INVALID_BIZTEL_TOKEN = 'INVALID_BIZTEL_TOKEN',
  BIZTEL_CONNECTION_ERROR = 'BIZTEL_CONNECTION_ERROR',
}

export const ALL_CUSTOM_ERROR_CODES: readonly CustomErrorCode[] =
  Object.values(CustomErrorCode)

export class CustomError extends Error {
  code: CustomErrorCode
  extensions: GraphQLErrorExtensions
  param?: ErrorParam

  constructor(
    code: CustomErrorCode,
    extensions: GraphQLErrorExtensions,
    message: string,
    param?: ErrorParam,
  ) {
    super()
    this.code = code
    this.extensions = extensions
    this.message = message
    this.param = param
  }
}

/** KPI の想定エラーを型・コード・完全一致のメッセージで判定する。 */
export function isKpiCustomError(
  error: unknown,
  code: CustomErrorCode,
  messages: readonly string[],
): error is CustomError {
  return (
    error instanceof CustomError &&
    error.code === code &&
    messages.includes(error.message)
  )
}

// 汎用コードは既存画面の CustomError 判定を変えないよう、対応するサービスだけで追加する。
const DEFAULT_CUSTOM_ERROR_CODES = ALL_CUSTOM_ERROR_CODES.filter(
  (code) =>
    code !== CustomErrorCode.MFA_RESET_INCOMPLETE &&
    code !== CustomErrorCode.RECENT_LOGIN_REQUIRED &&
    code !== CustomErrorCode.RATE_LIMITED &&
    code !== CustomErrorCode.PASSKEY_NOT_ALLOWED &&
    code !== CustomErrorCode.BAD_USER_INPUT &&
    code !== CustomErrorCode.UNAUTHENTICATED &&
    code !== CustomErrorCode.BAD_REQUEST &&
    code !== CustomErrorCode.NOT_FOUND,
)

/**
 * 非 2xx 応答の本文に入った GraphQL エラーを取り出す。
 *
 * apollo-angular 14 の HttpLink は非 2xx 応答の HttpErrorResponse を Apollo Client の
 * ServerError に変換して流すため、GraphQL エラー本体は ServerError.bodyText の JSON に入る
 * （backend が context 生成中に throw する RESTRICTED_IP_ERROR は status 500 で返る）。
 */
export function extractServerGraphQLErrors(
  error: unknown,
): readonly GraphQLFormattedError[] {
  if (!ServerError.is(error)) {
    return []
  }
  let body: unknown
  try {
    body = JSON.parse(error.bodyText)
  } catch {
    // プロキシ等が返す HTML のように JSON でない本文は GraphQL エラーを含まない
    return []
  }
  if (
    typeof body !== 'object' ||
    body == null ||
    !('errors' in body) ||
    !Array.isArray(body.errors)
  ) {
    return []
  }
  return body.errors
}

export const throwIfCustomError = (
  error: unknown,
  codes: readonly CustomErrorCode[] = DEFAULT_CUSTOM_ERROR_CODES,
) => {
  if (!error) {
    return
  }

  const restrictedError = extractServerGraphQLErrors(error).find(
    (e): e is Required<GraphQLFormattedError> =>
      e.extensions?.code === 'RESTRICTED_IP_ERROR',
  )

  if (restrictedError) {
    alert(
      'エラーが発生しました。ユーザー情報またはネットワーク設定をご確認ください。',
    )
    location.href = '/login'
    throw new CustomError(
      CustomErrorCode.RESTRICTED_IP_ERROR,
      restrictedError.extensions,
      restrictedError.message,
    )
  }

  if (!CombinedGraphQLErrors.is(error)) {
    return
  }

  if (!error.errors.length) {
    return
  }

  const index = error.errors.findIndex((err) =>
    codes.includes(err.extensions?.code as CustomErrorCode),
  )

  if (index !== -1) {
    const err = error.errors[index]
    const extensions = err.extensions as GraphQLErrorExtensions

    throw new CustomError(
      extensions.code as CustomErrorCode,
      extensions,
      err.message,
      extensions.param as ErrorParam,
    )
  } else {
    throw new Error(error.errors[0].message)
  }
}
